import { z } from 'zod';
import {
    type NodeSocketCfg,
} from './base-node';
import { baseConnectionCfgSchema, connectionNodeConfigSchema } from './connection.zod';
import type { NodeCfg } from '../nodes';
import { ioddDataTypeSchema } from '../../../../../connections/io-link-master.connection-config.zod';

export const ioLinkNodeCfgSchema = connectionNodeConfigSchema(baseConnectionCfgSchema.extend({
    kind: z.literal('ioLink'),
    controlValues: baseConnectionCfgSchema.shape.controlValues.merge(z.object({
        /** just for display purposes on flow nodes */
        modeName: z.string(),
        channelNumber: z.number(),
        /** keyed by data type id */
        dataTypes: z.record(ioddDataTypeSchema),
        /** this is commented elsewhere, but i am pretty sure the only way to conditionally map device operating modes is through a number which represents a value in an enum
         * If that is not true then we need to update this type
        */
        conditionValue: z.union([ z.literal('default'), z.number() ]),
        /** keyed by process data in id, value is the data type required by the pdi */
        processDataIn: z.record(z.union([ z.literal('string'), z.literal('number'), z.literal('boolean') ])),
        /** keyed by process data out id, value is either the data type the PDO item references or a primative data type */
        processDataOut: z.record(z.discriminatedUnion('kind', [
            z.object({
                dataTypeRef: z.string(),
                displayName: z.string(),
                kind: z.literal('dataRefLookup'),
            }),
            z.object({
                displayName: z.string(),
                dataType: ioddDataTypeSchema,
                kind: z.literal('selfDefined'),
            }),
        ])),
    })),
}));
interface IOLinkCfgSchema extends z.infer<typeof ioLinkNodeCfgSchema> {}

export const ioLinkNodeSocketCfg = {
    controlSockets: {
        inputs: (cfg: NodeCfg<'ioLink'>) => {
            const ret: {
                writeProcessDataOut?: 'event';
            } = {};
            if (Object.keys(cfg.controlValues.processDataOut).length) {
                ret.writeProcessDataOut = 'event';
            }
            return ret;
        },
        outputs: (cfg: NodeCfg<'ioLink'>) => {
            const ret: {
                processDataInReceieved?: 'event';
            } = {};
            if (Object.keys(cfg.controlValues.processDataIn).length) {
                ret.processDataInReceieved = 'event';
            }
            return ret;
        },
    },
    dataSockets: {
        outputs: (cfg: NodeCfg<'ioLink'>) => {
            const ret: Record<string, 'string' | 'number' | 'boolean'> = {};
            for (const [ processDataInId, dataType ] of Object.entries(cfg.controlValues.processDataIn)) {
                ret[processDataInId] = dataType;
            }
            return ret;
        },
        inputs: (cfg: NodeCfg<'ioLink'>) => {
            const ret: Record<string, 'string' | 'number' | 'boolean'> = {};
            for (const [ processDataOutId, dataTypeDetails ] of Object.entries(cfg.controlValues.processDataOut)) {
                if (dataTypeDetails.kind === 'dataRefLookup') {
                    const dataRef = cfg.controlValues.dataTypes[dataTypeDetails.dataTypeRef];
                    if (!dataRef || dataRef.dataKind === 'object') {
                        throw new Error('Data ref either not found or is of unsupported type');
                    }
                    ret[processDataOutId] = dataRef.dataKind;
                } else {
                    if (dataTypeDetails.dataType.dataKind === 'object') {
                        throw new Error('Static data type is of unsupported data type');
                    }
                    ret[processDataOutId] = dataTypeDetails.dataType.dataKind;
                }
            }
            return ret;
        },
    },
} satisfies NodeSocketCfg<IOLinkCfgSchema>;
